import React, { useState } from 'react'
import { Formik } from 'formik'
import { IconX } from '@tabler/icons-react'
import { useDispatch } from 'react-redux'
import classNames from 'classnames'
import { isEmpty, isString } from 'lodash'

import { Pill } from '@/components/pills/Pill'
import { Button } from '@/components/buttons/Button'
import { ProgressBar } from '@/components/navigation/ProgressBar'
import { closeModal } from '@/reducers/ui/ui.redux'

import { Modal } from './Modal'
import './WizardModal.scss'

export const WizardModal = ({
  description,
  initialValues = {},
  isLoading = false,
  // disables the buttons based on external conditions
  disableButtons,
  rightButtonSubmitLabel = 'Submit',
  onPageChange,
  onSave,
  onClose,
  pages = [],
  title,
  validationSchema,
  size = 'tiny',
  startPage = 0,
  playbook = null,
  enableButton = false,
  scrollable = false,
  validate,
  alwaysEnablePreviousButton = false,
  initialTouched,
  innerRef = () => {},
  ...otherProps
}) => {
  const dispatch = useDispatch()
  const [currentPage, setCurrentPage] = useState(startPage)
  const lastPage = pages.length - 1
  // Based on the UI Redux store, all modals should have the same close function
  const handleClose = () => {
    dispatch(closeModal())
  }

  const nextPage = (values, setFieldValue) => {
    if (currentPage === lastPage) {
      return
    }
    const nextPageIndex = currentPage + 1
    if (onPageChange) onPageChange(values, nextPageIndex, setFieldValue)
    setCurrentPage(nextPageIndex)
  }

  const prevPage = (values, setFieldValue) => {
    if (currentPage === 0) {
      return
    }
    const prevPageIndex = currentPage - 1
    if (onPageChange) onPageChange(values, prevPageIndex, setFieldValue)
    setCurrentPage(prevPageIndex)
  }

  const goToPageNumber = (value) => {
    setCurrentPage(value)
  }

  const rightButton = {
    label: currentPage === lastPage ? rightButtonSubmitLabel : 'Continue',
    fn:
      currentPage === lastPage
        ? (handleSubmit) => handleSubmit()
        : (handleSubmit, values, setFieldValue) => nextPage(values, setFieldValue),
  }

  const shouldDisableButton = (loading, errors, touched, shouldDisableButtons) => {
    if (enableButton) {
      return !enableButton
    }

    if (loading || !isEmpty(errors) || isEmpty(touched) || shouldDisableButtons) {
      return true
    }

    return false
  }

  return (
    <Modal
      aria-label={isString(title) ? title : undefined}
      className="wizard-modal"
      data-testid="youre-a-wizard-harry"
      onClose={onClose || handleClose}
      size={size}
      {...otherProps}
    >
      <header className="modal-header">
        <h1 className="flex-align-center medium-gap">
          {title} {playbook && <Pill small>{playbook?.name}</Pill>}
        </h1>
        <Button
          small
          square
          basic
          onClick={onClose || handleClose}
          data-testid="basic-modal-close-button"
        >
          <IconX />
        </Button>
      </header>
      <Formik
        initialValues={initialValues}
        initialTouched={initialTouched}
        validate={validate ? (values) => validate(values, currentPage) : null}
        validationSchema={validationSchema}
        onSubmit={onSave}
        enableReinitialize
        innerRef={innerRef}
      >
        {({ handleSubmit, values, errors, touched, setFieldValue, ...props }) => (
          <>
            <div
              className={classNames('modal-body', 'wizard-modal-scrollable', {
                scrolling: scrollable,
              })}
            >
              {description && <p>{description}</p>}
              {/* Expects pages to be a list of functions so we can pass form values */}
              {/* as well as the goToPageNumber function. */}
              {/* Check the pages list on ScorecardWizard.js for an example */}
              {pages.length
                ? pages[currentPage](values, goToPageNumber, errors, setFieldValue, props)
                : null}
            </div>
            <div className="modal-actions">
              <div className="wizard-modal-actions">
                <div>
                  {currentPage === 0 || (
                    <Button
                      data-testid="left-wizard-button"
                      onClick={() => prevPage(values, setFieldValue)}
                      disabled={
                        // don't want to disable close modal button
                        alwaysEnablePreviousButton
                          ? false
                          : shouldDisableButton(isLoading, errors, touched, disableButtons)
                      }
                    >
                      Previous
                    </Button>
                  )}
                </div>
                <div>
                  <ProgressBar totalPages={pages.length} currentPage={currentPage + 1} isProgress />
                </div>
                <Button
                  data-testid="right-wizard-button"
                  brand
                  type={currentPage === lastPage ? 'submit' : 'button'}
                  onClick={() => rightButton.fn(handleSubmit, values, setFieldValue)}
                  loading={isLoading}
                  disabled={shouldDisableButton(isLoading, errors, touched, disableButtons)}
                >
                  {rightButton.label}
                </Button>
              </div>
            </div>
          </>
        )}
      </Formik>
    </Modal>
  )
}
